import React from 'react'
import { Link } from 'react-router'
import { Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@emotion/react'
import { flagPath } from '../../../components/entities/CompaniesGrid'
import { normalizedName } from '../../../components/entities/CompanyName'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../../packages/utils/numberFormatters'
import AdvisorList from '../../issues/AdvisorList'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../i18n/localeUtils'
import { FactsTable, FactsTableRow } from '../../components/FactsTable'
import { getConvertibleLoanAmount, getConvertibleStatusLabel } from '../../../components/utils/convertibleUtils'

function ConvertibleBasicInfo({ convertible }: { convertible: ConvertibleEntity }) {
    const { t } = useTranslation()
    const theme = useTheme()
    const { language: locale } = useAppLocale()
    const tableRows = []

    const loanAmount = getConvertibleLoanAmount(convertible)
    const statusLabel = getConvertibleStatusLabel(convertible)

    tableRows.push(
        <FactsTableRow key={'company'} label={t('entities.company.singular')}>
            <Link to={`/companies/${convertible.companyId}`}>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {convertible.companyCountryCode && (
                        <Tooltip title={convertible.companyCountryCode}>
                            <div style={{ display: 'inline-block' }}>
                                <img
                                    src={flagPath(convertible.companyCountryCode)}
                                    width={25}
                                    style={{ display: 'block' }}
                                />
                            </div>
                        </Tooltip>
                    )}
                    <Typography>{normalizedName(convertible.companyName)}</Typography>
                </Stack>
            </Link>
        </FactsTableRow>,
    )

    tableRows.push(
        <FactsTableRow key={'convertibleName'} label={t('site.table.name')}>
            {convertible.convertibleName}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key='convertibleStatus' label={t('pages.convertibles.status.label')}>
            <Stack
                direction='row'
                spacing={1}
                sx={{
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        height: '10px',
                        width: '10px',
                        margin: 0,
                        backgroundColor: convertible.completed
                            ? theme.palette.entityStatus.complete
                            : theme.palette.entityStatus.incomplete,
                        borderRadius: '50%',
                        display: 'inline-block',
                    }}
                />
                <Typography>{statusLabel}</Typography>
            </Stack>
        </FactsTableRow>,
    )
    if (convertible.creditLine !== null) {
        tableRows.push(
            <FactsTableRow key='creditLine' label={t('pages.convertibles.creditLine')}>
                {formatBigMoney(convertible.creditLine, { locale, currencySymbol: convertible.currency })}
            </FactsTableRow>,
        )
    }
    tableRows.push(
        <FactsTableRow key='loanAmount' label={t('pages.convertibles.loanAmount')}>
            {loanAmount !== null ? formatBigMoney(loanAmount, { locale, currencySymbol: convertible.currency }) : '-'}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key='maturityDate' label={t('pages.convertibles.maturityDate')}>
            <Stack spacing={1}>
                <Typography>{formatDateYYYYMMDD(convertible.maturityDate, { locale })}</Typography>
                {convertible.convertAnytime ? (
                    <Typography>{t('pages.convertibles.convertAnytime')}</Typography>
                ) : null}
            </Stack>
        </FactsTableRow>,
    )
    if (convertible.noncashConsideration) {
        tableRows.push(
            <FactsTableRow key='noncashConsideration' label={t('pages.convertibles.noncashConsideration')}>
                {convertible.noncashConsideration}
            </FactsTableRow>,
        )
    }
    tableRows.push(
        <FactsTableRow
            key='interestRaste'
            label={
                <Stack>
                    {convertible.arrangementFee !== null ? (
                        <Typography>{t('pages.convertibles.interest.arrangementFee')}</Typography>
                    ) : null}
                    <Typography>
                        {convertible.monthlyInterest
                            ? t('pages.convertibles.interest.monthly')
                            : convertible.annualInterest
                              ? t('pages.convertibles.interest.annual')
                              : t('pages.convertibles.interest.label')}
                    </Typography>
                </Stack>
            }
        >
            <Stack>
                {convertible.arrangementFee !== null ? (
                    <Typography>{formatPercentage(convertible.arrangementFee, { locale, minDecimals: 0 })}</Typography>
                ) : null}
                <Typography>
                    {convertible.stibor ? t('pages.convertibles.interest.stiborPlus') : ''}
                    {convertible.monthlyInterest
                        ? formatPercentage(convertible.monthlyInterest, { locale, minDecimals: 0 })
                        : convertible.annualInterest
                          ? formatPercentage(convertible.annualInterest, { locale, minDecimals: 0 })
                          : t('pages.convertibles.interest.interestFree')}
                </Typography>
            </Stack>
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key='conversionPrice' label={t('pages.convertibles.conversionPrice.label')}>
            {convertible.fixedConversionPrice ? (
                formatMoney(convertible.fixedConversionPrice, {
                    minDecimals: 2,
                    maxDecimals: 5,
                    locale,
                })
            ) : (
                <div>
                    <Typography variant='body1'>
                        {`${t('site.pricing.vwapDiscount')} ${
                            convertible.vwapDiscount
                                ? formatPercentage(convertible.vwapDiscount, {
                                      locale,
                                      minDecimals: 0,
                                  })
                                : '-'
                        }${convertible.vwapDays && convertible.vwapDays > 0 ? ` (${convertible.vwapDays} ${convertible.vwapDays > 1 ? t('site.pricing.days') : t('site.pricing.day')})` : ''}`}
                    </Typography>
                    <Typography variant='body1'>
                        {t('site.pricing.min')}{' '}
                        {convertible?.priceMin
                            ? formatMoney(convertible?.priceMin, {
                                  minDecimals: 2,
                                  maxDecimals: 5,
                                  locale,
                              })
                            : t('site.pricing.parValue')}
                        , {t('site.pricing.max')}{' '}
                        {convertible?.priceMax
                            ? formatMoney(convertible?.priceMax, {
                                  minDecimals: 2,
                                  maxDecimals: 5,
                                  locale,
                              })
                            : '-'}
                    </Typography>
                </div>
            )}
        </FactsTableRow>,
    )

    const financialAdvisorExists = convertible.advisors.find((advisor) => advisor.type === 'financial_advisor')
    const legalAdvisorExists = convertible.advisors.find((advisor) => advisor.type === 'legal_advisor')

    tableRows.push(
        <FactsTableRow key='financialAdvisors' label={t('pages.convertibles.advisors.financial')}>
            {financialAdvisorExists ? <AdvisorList advisors={convertible.advisors} type='financial_advisor' /> : '-'}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key='legalAdvisors' label={t('pages.convertibles.advisors.legal')}>
            {legalAdvisorExists ? <AdvisorList advisors={convertible.advisors} type='legal_advisor' /> : '-'}
        </FactsTableRow>,
    )

    return <FactsTable rows={tableRows} />
}
export default ConvertibleBasicInfo
